import React, { useState } from "react";
import { MapPin, Crosshair, ChevronDown, X, Loader2 } from "lucide-react";
import { GHANA_CITIES } from "../hooks/useGeo";

/**
 * Compact location pill used on every search surface. Shows the detected GPS
 * location (nearest city) or a manually picked city, with a dropdown fallback
 * when GPS is denied/unavailable.
 */
export const LocationPicker = ({ geo, status, detect, setManual, clear, accent = "#7C3AED" }) => {
  const [open, setOpen] = useState(false);

  const label =
    status === "locating" ? "Locating…"
    : geo?.source === "gps" ? `Near ${geo.city || "you"}`
    : geo?.source === "manual" ? geo.city
    : "Set location";

  return (
    <div className="relative inline-flex items-center gap-1.5" data-testid="location-picker">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="location-picker-toggle"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border transition-colors"
        style={{ borderColor: `${accent}66`, backgroundColor: `${accent}1A`, color: accent }}
      >
        {status === "locating" ? <Loader2 size={11} className="animate-spin" /> : <MapPin size={11} />}
        {label}
        <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {geo && (
        <button type="button" onClick={clear} data-testid="location-picker-clear" title="Clear location"
                className="p-1 rounded-full text-[#9CA3D4] hover:text-white">
          <X size={11} />
        </button>
      )}

      {open && (
        <div className="absolute top-full left-0 mt-2 z-40 w-56 rounded-xl border border-[#26263A] bg-[#13131E] shadow-2xl p-2 space-y-1"
             data-testid="location-picker-menu">
          <button
            type="button"
            onClick={() => { detect(); setOpen(false); }}
            data-testid="location-picker-gps"
            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-bold text-white hover:bg-[#1A1A27]"
          >
            <Crosshair size={12} style={{ color: accent }} /> Use my current location
          </button>
          {status === "denied" && (
            <div className="px-2.5 text-[10px] text-[#F59E0B]">GPS blocked. pick your city below.</div>
          )}
          <div className="max-h-48 overflow-y-auto divide-y divide-[#26263A]/50">
            {GHANA_CITIES.map((c) => (
              <button
                key={c.name}
                type="button"
                onClick={() => { setManual(c.name); setOpen(false); }}
                data-testid={`location-picker-city-${c.name.toLowerCase().replace(/\s/g, "-")}`}
                className={`w-full text-left px-2.5 py-1.5 text-xs hover:bg-[#1A1A27] rounded-md ${geo?.city === c.name ? "text-white font-bold" : "text-[#9CA3D4]"}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationPicker;
